import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const guideArticles = [
  { name: 'Kompletter Guide', path: '/artikel/oopbuy-spreadsheet-guide' },
  { name: 'Schuhe Guide', path: '/artikel/oopbuy-spreadsheet-schuhe' },
  { name: 'Sicherheit Guide', path: '/artikel/oopbuy-spreadsheet-sicherheit' },
  { name: 'Vergleich 2026', path: '/artikel/oopbuy-spreadsheet-vergleich' },
  { name: 'Anfänger Guide', path: '/artikel/oopbuy-spreadsheet-anfaenger' },
];

export default function Navbar() {
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const [mobileGuideExpanded, setMobileGuideExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catDropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catDropdownRef.current && !catDropdownRef.current.contains(e.target as Node)) {
        setCatDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-900 whitespace-nowrap cursor-pointer"
          >
            OOPBuy Spreadsheet
          </Link>

          <div className="hidden md:flex items-center gap-1 md:gap-3">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line text-sm"></i>
              Startseite
            </Link>

            <div className="relative" ref={catDropdownRef}>
              <button
                onClick={() => {
                  setCatDropdownOpen(!catDropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                Kategorien
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${catDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {catDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 bg-background-50 border border-background-200/70 rounded-lg py-2 max-h-80 overflow-y-auto shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setCatDropdownOpen(false)}
                    >
                      <i className="ri-price-tag-3-line text-xs text-foreground-400"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={guideDropdownRef}>
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setCatDropdownOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line text-sm"></i>
                Einkaufs-Guide
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 bg-background-50 border border-background-200/70 rounded-lg py-2 shadow-sm">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setGuideDropdownOpen(false)}
                    >
                      <i className="ri-article-line text-xs text-foreground-400"></i>
                      {article.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-2 text-sm text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Sicherheit
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              oopbuy spreadsheet
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menü öffnen"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line"></i>
              Startseite
            </Link>

            <div>
              <button
                className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-price-tag-3-line"></i>
                  Kategorien
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatExpanded ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatExpanded && (
                <div className="ml-5 mt-1 grid grid-cols-2 gap-1 pb-1 border-l-2 border-background-200 pl-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-1.5 px-2 py-2 text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded transition-colors cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <i className="ri-arrow-right-up-line text-foreground-400"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-primary-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileGuideExpanded(!mobileGuideExpanded)}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-book-open-line"></i>
                  Einkaufs-Guide
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideExpanded ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileGuideExpanded && (
                <div className="ml-5 mt-1 pb-1 border-l-2 border-primary-200 pl-3 flex flex-col gap-1">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      className="flex items-center gap-1.5 px-2 py-2 text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded transition-colors cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <i className="ri-article-line text-foreground-400"></i>
                      {article.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-shield-check-line"></i>
              Sicherheit
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 px-4 py-3 text-sm font-medium bg-foreground-900 text-background-50 rounded-full text-center hover:bg-foreground-800 transition-colors cursor-pointer flex items-center justify-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              oopbuy spreadsheet
              <i className="ri-external-link-line text-xs"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}